import {
  EllipsisVertical,
  MicOff,
  ScreenShareOff,
  ShieldCheck,
  ShieldOff,
  UserX,
} from 'lucide-react';
import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type { PanelModerationAction } from '../realtime/frames';

/** Icons of the panel actions; the kick item is destructive and needs no extra mark. */
const ACTION_ICONS: Readonly<Record<PanelModerationAction, JSX.Element>> = {
  mute: <MicOff className="h-4 w-4" />,
  mute_all: <MicOff className="h-4 w-4" />,
  stop_share: <ScreenShareOff className="h-4 w-4" />,
  kick: <UserX className="h-4 w-4" />,
  promote: <ShieldCheck className="h-4 w-4" />,
  demote: <ShieldOff className="h-4 w-4" />,
};

export interface ModerationMenuProps {
  readonly identity: string;
  /** Display name used in the accessible labels and the kick confirmation question. */
  readonly displayName: string;
  /** Actions the local role permits against this row (moderationActionsFor). */
  readonly actions: readonly PanelModerationAction[];
  /** Dispatches one action to the realtime channel; kick first asks the inline confirmation. */
  readonly onAction: (action: PanelModerationAction, identity: string) => void;
}

/**
 * The kebab menu of one participants-panel row: the moderation actions the local user's role
 * permits for that participant. Remove (kick) asks a short inline confirmation before the
 * frame goes out; everything else dispatches immediately. Rendered only when the action list
 * is non-empty.
 */
export function ModerationMenu({
  identity,
  displayName,
  actions,
  onAction,
}: ModerationMenuProps): JSX.Element | null {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  if (actions.length === 0) {
    return null;
  }

  if (confirming) {
    return (
      <span
        data-testid={`mod-confirm-${identity}`}
        className="flex min-w-0 items-center justify-end gap-1.5"
      >
        <span className="truncate text-[11px] font-medium text-red-200">
          {t('moderation.confirm_remove', { name: displayName })}
        </span>
        <button
          type="button"
          data-testid={`mod-confirm-yes-${identity}`}
          aria-label={t('moderation.confirm_yes_aria', { name: displayName })}
          onClick={() => {
            setConfirming(false);
            onAction('kick', identity);
          }}
          className="shrink-0 rounded-md bg-red-500/90 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-red-500"
        >
          {t('moderation.confirm_yes')}
        </button>
        <button
          type="button"
          data-testid={`mod-confirm-no-${identity}`}
          aria-label={t('moderation.confirm_no_aria')}
          onClick={() => {
            setConfirming(false);
          }}
          className="shrink-0 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:bg-white/10"
        >
          {t('common.cancel')}
        </button>
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`mod-menu-${identity}`}
          aria-label={t('moderation.menu_aria', { name: displayName })}
          title={t('moderation.menu_aria', { name: displayName })}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-zinc-400 opacity-0 transition-colors group-hover:opacity-100 focus:opacity-100 hover:bg-zinc-700 hover:text-white data-[state=open]:opacity-100"
        >
          <EllipsisVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-white/10 bg-zinc-900">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action}
            data-testid={`mod-action-${action}-${identity}`}
            variant={action === 'kick' ? 'destructive' : 'default'}
            onSelect={() => {
              if (action === 'kick') {
                setConfirming(true);
                return;
              }
              onAction(action, identity);
            }}
          >
            {ACTION_ICONS[action]}
            {t(`moderation.action_${action}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
